import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useOrganizations } from '../../hooks/useOrganizations'
import { useSsoConnections } from '../../hooks/useSsoConnections'
import { useApplications } from '../../hooks/useApplications'
import { useImageUpload } from '../../hooks/useImageUpload'
import type { OrganizationDomain, OrganizationMember } from '../../types/database'
import { supabase } from '../../lib/supabase'

interface PendingInvite {
  id: string
  invite_email: string
  invite_sent_at: string
}
import {
  VStack,
  HStack,
  Card,
  Heading,
  Text,
  Button,
  IconButton,
  Input,
  Switch,
  Badge,
  Table,
  FileUpload,
  Animate,
  Tabs,
  TabPanels,
  TabPanel,
  ConfirmDialog,
  Banner
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Save20Regular,
  Delete20Regular,
  Add20Regular,
  Checkmark20Regular,
  Globe20Regular,
  People20Regular,
  Key20Regular,
  Info20Regular,
  Building20Regular,
  Person20Regular,
  Mail20Regular,
  Copy20Regular
} from '@fluentui/react-icons'

export function OrganizationForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const {
    createOrganization,
    updateOrganization,
    getOrganization,
    addDomain,
    removeDomain,
    verifyDomain,
    getDomains,
    getMembers,
    removeMember,
    updateMemberRole
  } = useOrganizations()
  const { connections, loadConnections } = useSsoConnections()
  useApplications() // Prefetch for SSO connection form
  const { uploading, uploadImage, deleteImage } = useImageUpload()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [isActive, setIsActive] = useState(true)

  const [domains, setDomains] = useState<OrganizationDomain[]>([])
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [newDomain, setNewDomain] = useState('')

  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<{ name?: string; slug?: string; domain?: string }>({})
  const [activeTab, setActiveTab] = useState('general')

  const [showDeleteDomainConfirm, setShowDeleteDomainConfirm] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState<OrganizationDomain | null>(null)
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(false)
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null)

  // Admin invitation state
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])
  const [adminEmail, setAdminEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)

  useEffect(() => {
    if (isEdit && id) {
      loadOrganizationData()
    }
  }, [isEdit, id])

  const loadOrganizationData = async () => {
    if (!id) return

    const org = await getOrganization(id)
    if (org) {
      setName(org.name)
      setSlug(org.slug)
      setLogoUrl(org.logo_url || '')
      setIsActive(org.is_active)

      // Load domains
      const orgDomains = await getDomains(id)
      setDomains(orgDomains)

      // Load members
      const orgMembers = await getMembers(id)
      setMembers(orgMembers)

      // Load SSO connections
      await loadConnections(id)

      // Load pending admin invites
      const { data: invites } = await supabase.rpc('get_org_pending_invites', {
        p_organization_id: id
      })
      setPendingInvites(invites || [])
    }
  }

  const handleInviteAdmin = async () => {
    if (!adminEmail.trim() || !id) return

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(adminEmail.trim())) {
      setErrors(prev => ({ ...prev, domain: 'Email invalide' }))
      return
    }

    setInviting(true)
    setInviteSuccess(null)
    setInviteLink(null)

    try {
      const { data: token, error } = await supabase.rpc('create_org_admin_invite', {
        p_organization_id: id,
        p_email: adminEmail.trim()
      })

      if (error) throw error

      const link = `${window.location.origin}/invite/${token}`
      setInviteLink(link)
      setInviteSuccess(`Invitation créée pour ${adminEmail}`)
      setAdminEmail('')

      // Reload pending invites
      const { data: invites } = await supabase.rpc('get_org_pending_invites', {
        p_organization_id: id
      })
      setPendingInvites(invites || [])
    } catch (err: any) {
      console.error('Error inviting admin:', err)
      setErrors(prev => ({ ...prev, domain: err.message }))
    } finally {
      setInviting(false)
    }
  }

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await supabase.rpc('cancel_org_invite', { p_member_id: inviteId })
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId))
    } catch (err) {
      console.error('Error canceling invite:', err)
    }
  }

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink)
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleNameChange = (value: string) => {
    setName(value)
    if (!isEdit && !slug) {
      setSlug(generateSlug(value))
    }
    if (errors.name) setErrors(prev => ({ ...prev, name: undefined }))
  }

  const handleSubmit = async () => {
    const newErrors: { name?: string; slug?: string } = {}

    if (!name.trim()) {
      newErrors.name = 'Le nom de l\'organisation est requis'
    }

    if (!slug.trim()) {
      newErrors.slug = 'L\'identifiant est requis'
    } else if (!/^[a-z0-9-]+$/.test(slug)) {
      newErrors.slug = 'L\'identifiant ne peut contenir que des lettres minuscules, chiffres et tirets'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setSaving(true)

    if (isEdit && id) {
      const success = await updateOrganization(id, {
        name: name.trim(),
        slug: slug.trim(),
        logo_url: logoUrl || undefined,
        is_active: isActive
      })
      if (success) {
        navigate('/platform/organizations')
      } else {
        setErrors({ name: 'Échec de la mise à jour de l\'organisation' })
      }
    } else {
      const result = await createOrganization({
        name: name.trim(),
        slug: slug.trim(),
        logo_url: logoUrl || undefined
      })
      if (result) {
        navigate(`/platform/organizations/${result.id}`)
      } else {
        setErrors({ name: 'Échec de la création de l\'organisation' })
      }
    }

    setSaving(false)
  }

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      setErrors(prev => ({ ...prev, domain: 'Le domaine est requis' }))
      return
    }

    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}$/
    if (!domainRegex.test(newDomain.trim())) {
      setErrors(prev => ({ ...prev, domain: 'Format de domaine invalide (ex: example.com)' }))
      return
    }

    if (!id) return

    const result = await addDomain(id, newDomain.trim())
    if (result) {
      setDomains(prev => [...prev, result])
      setNewDomain('')
      setErrors(prev => ({ ...prev, domain: undefined }))
    }
  }

  const handleDeleteDomain = async () => {
    if (!selectedDomain) return
    const success = await removeDomain(selectedDomain.id)
    if (success) {
      setDomains(prev => prev.filter(d => d.id !== selectedDomain.id))
    }
    setShowDeleteDomainConfirm(false)
    setSelectedDomain(null)
  }

  const handleVerifyDomain = async (domain: OrganizationDomain) => {
    const success = await verifyDomain(domain.id)
    if (success) {
      setDomains(prev => prev.map(d =>
        d.id === domain.id ? { ...d, verified: true, verified_at: new Date().toISOString() } : d
      ))
    }
  }

  const handleRemoveMember = async () => {
    if (!selectedMember) return
    const success = await removeMember(selectedMember.id)
    if (success) {
      setMembers(prev => prev.filter(m => m.id !== selectedMember.id))
    }
    setShowRemoveMemberConfirm(false)
    setSelectedMember(null)
  }

  const handleToggleMemberRole = async (member: OrganizationMember) => {
    const newRole = member.role === 'admin' ? 'member' : 'admin'
    const success = await updateMemberRole(member.id, newRole)
    if (success) {
      setMembers(prev => prev.map(m =>
        m.id === member.id ? { ...m, role: newRole } : m
      ))
    }
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <HStack gap="md" style={{ alignItems: 'center' }}>
            <IconButton
              icon={<ArrowLeft20Regular />}
              onClick={() => navigate('/platform/organizations')}
            />
            <div>
              <Heading level={2}>{isEdit ? 'Modifier l\'organisation' : 'Nouvelle organisation'}</Heading>
              <Text color="muted">
                {isEdit ? 'Modifiez les paramètres de l\'organisation' : 'Créez une nouvelle organisation pour le SSO entreprise'}
              </Text>
            </div>
          </HStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          <Tabs
            tabs={[
              { id: 'general', label: 'Général', icon: <Info20Regular /> },
              ...(isEdit ? [
                { id: 'admins', label: 'Administrateurs', icon: <Person20Regular /> },
                { id: 'domains', label: 'Domaines', icon: <Globe20Regular /> },
                { id: 'sso', label: 'Connexions SSO', icon: <Key20Regular /> },
                { id: 'members', label: 'Membres', icon: <People20Regular /> }
              ] : [])
            ]}
            active={activeTab}
            onChange={setActiveTab}
            stretchLine
          />

          <div style={{ marginTop: '1.5rem' }}>
            <TabPanels active={activeTab}>
              {/* General Tab */}
              <TabPanel id="general" active={activeTab}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                      <Building20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={5}>Informations générales</Heading>
                    </HStack>

                    <Input
                      label="Nom de l'organisation"
                      placeholder="Thales Group"
                      value={name}
                      onChange={(v) => handleNameChange(v as string)}
                      error={errors.name}
                    />

                    <div>
                      <Input
                        label="Identifiant (slug)"
                        placeholder="thales"
                        value={slug}
                        onChange={(v) => {
                          setSlug((v as string).toLowerCase().replace(/[^a-z0-9-]/g, ''))
                          if (errors.slug) setErrors(prev => ({ ...prev, slug: undefined }))
                        }}
                        error={errors.slug}
                      />
                      <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                        Utilisé dans les URLs. Lettres minuscules, chiffres et tirets uniquement.
                      </Text>
                    </div>

                    <div>
                      <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
                        Logo de l'organisation
                      </Text>
                      {logoUrl ? (
                        <Card variant="outlined" padding="md">
                          <HStack gap="md" style={{ alignItems: 'center' }}>
                            <img
                              src={logoUrl}
                              alt="Logo"
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: 'var(--radius-md)'
                              }}
                            />
                            <VStack gap="md" style={{ flex: 1 }}>
                              <Text size="sm" color="muted">Logo téléchargé</Text>
                              <Button
                                variant="danger"
                                size="sm"
                                icon={<Delete20Regular />}
                                onClick={async () => {
                                  await deleteImage(logoUrl)
                                  setLogoUrl('')
                                }}
                              >
                                Supprimer
                              </Button>
                            </VStack>
                          </HStack>
                        </Card>
                      ) : (
                        <FileUpload
                          onFilesSelected={async (files: File[]) => {
                            if (files.length > 0) {
                              const url = await uploadImage(files[0])
                              if (url) setLogoUrl(url)
                            }
                          }}
                          accept="image/*"
                          maxSize={5 * 1024 * 1024}
                        />
                      )}
                      {uploading && (
                        <Text size="sm" color="muted" style={{ marginTop: '0.5rem' }}>
                          Téléchargement en cours...
                        </Text>
                      )}
                    </div>

                    {isEdit && (
                      <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text weight="medium">Statut de l'organisation</Text>
                          <Text size="sm" color="muted">
                            {isActive ? 'Le SSO est actif pour cette organisation' : 'Le SSO est désactivé'}
                          </Text>
                        </div>
                        <Switch
                          checked={isActive}
                          onChange={setIsActive}
                        />
                      </HStack>
                    )}
                  </VStack>
                </Card>
              </TabPanel>

              {/* Admins Tab */}
              {isEdit && (
                <TabPanel id="admins" active={activeTab}>
                  <Card padding="lg">
                    <VStack gap="md">
                      <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                        <Person20Regular style={{ color: 'var(--brand-primary)' }} />
                        <Heading level={5}>Administrateurs de l'organisation</Heading>
                      </HStack>

                      <Text size="sm" color="muted">
                        Invitez des administrateurs qui pourront gérer le SSO et les domaines via leur propre portail.
                      </Text>

                      {/* Invite Form */}
                      <Card variant="outlined" padding="md">
                        <VStack gap="sm">
                          <Text weight="medium">Inviter un administrateur</Text>
                          <HStack gap="sm">
                            <div style={{ flex: 1 }}>
                              <Input
                                placeholder="admin@entreprise.com"
                                type="email"
                                value={adminEmail}
                                onChange={(v) => setAdminEmail(v as string)}
                                icon={<Mail20Regular />}
                              />
                            </div>
                            <Button
                              icon={<Add20Regular />}
                              onClick={handleInviteAdmin}
                              loading={inviting}
                              disabled={!adminEmail.trim()}
                            >
                              Inviter
                            </Button>
                          </HStack>

                          {inviteSuccess && (
                            <Banner variant="success" title="Invitation créée" onDismiss={() => setInviteSuccess(null)}>
                              <VStack gap="sm">
                                <Text size="sm">{inviteSuccess}</Text>
                                {inviteLink && (
                                  <HStack gap="sm" style={{ alignItems: 'center' }}>
                                    <code style={{
                                      fontSize: '12px',
                                      backgroundColor: 'var(--bg-tertiary)',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: 'var(--radius-sm)',
                                      wordBreak: 'break-all'
                                    }}>
                                      {inviteLink}
                                    </code>
                                    <IconButton
                                      icon={<Copy20Regular />}
                                      size="sm"
                                      onClick={copyInviteLink}
                                    />
                                  </HStack>
                                )}
                              </VStack>
                            </Banner>
                          )}
                        </VStack>
                      </Card>

                      {/* Pending Invites */}
                      {pendingInvites.length > 0 && (
                        <div>
                          <Text weight="medium" style={{ marginBottom: '0.5rem' }}>
                            Invitations en attente ({pendingInvites.length})
                          </Text>
                          <Table
                            columns={[
                              {
                                key: 'email',
                                header: 'Email',
                                render: (_, row: PendingInvite) => <Text>{row.invite_email}</Text>
                              },
                              {
                                key: 'sent',
                                header: 'Envoyée le',
                                render: (_, row: PendingInvite) => (
                                  <Text size="sm" color="muted">
                                    {new Date(row.invite_sent_at).toLocaleDateString('fr-FR')}
                                  </Text>
                                )
                              },
                              {
                                key: 'actions',
                                header: '',
                                render: (_, row: PendingInvite) => (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleCancelInvite(row.id)}
                                  >
                                    Annuler
                                  </Button>
                                )
                              }
                            ]}
                            data={pendingInvites}
                          />
                        </div>
                      )}

                      {/* Portal Link */}
                      <Banner variant="info" title="Portail administrateur">
                        <VStack gap="sm">
                          <Text size="sm">
                            Une fois invité, l'administrateur pourra accéder au portail self-service :
                          </Text>
                          <code style={{ fontSize: '13px' }}>
                            {window.location.origin}/org/{slug}
                          </code>
                        </VStack>
                      </Banner>
                    </VStack>
                  </Card>
                </TabPanel>
              )}

              {/* Domains Tab */}
              {isEdit && (
                <TabPanel id="domains" active={activeTab}>
                  <Card padding="lg">
                    <VStack gap="md">
                      <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                        <Globe20Regular style={{ color: 'var(--brand-primary)' }} />
                        <Heading level={5}>Domaines email</Heading>
                      </HStack>

                      <Text size="sm" color="muted">
                        Les utilisateurs avec ces domaines email seront redirigés vers le SSO de cette organisation.
                      </Text>

                      <HStack gap="sm">
                        <div style={{ flex: 1 }}>
                          <Input
                            placeholder="exemple.com"
                            value={newDomain}
                            onChange={(v) => {
                              setNewDomain(v as string)
                              if (errors.domain) setErrors(prev => ({ ...prev, domain: undefined }))
                            }}
                            error={errors.domain}
                          />
                        </div>
                        <Button icon={<Add20Regular />} onClick={handleAddDomain}>
                          Ajouter
                        </Button>
                      </HStack>

                      {domains.length === 0 ? (
                        <Card variant="outlined" padding="lg">
                          <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                            <Globe20Regular style={{ fontSize: '32px', color: 'var(--text-muted)' }} />
                            <Text color="muted">Aucun domaine configuré</Text>
                          </VStack>
                        </Card>
                      ) : (
                        <Table
                          columns={[
                            {
                              key: 'domain',
                              header: 'Domaine',
                              render: (_, row: OrganizationDomain) => (
                                <Text weight="medium">@{row.domain}</Text>
                              )
                            },
                            {
                              key: 'status',
                              header: 'Statut',
                              render: (_, row: OrganizationDomain) => (
                                <Badge variant={row.verified ? 'success' : 'warning'}>
                                  {row.verified ? 'Vérifié' : 'Non vérifié'}
                                </Badge>
                              )
                            },
                            {
                              key: 'actions',
                              header: '',
                              render: (_, row: OrganizationDomain) => (
                                <HStack gap="xs">
                                  {!row.verified && (
                                    <IconButton
                                      icon={<Checkmark20Regular />}
                                      size="sm"
                                      onClick={() => handleVerifyDomain(row)}
                                      title="Marquer comme vérifié"
                                    />
                                  )}
                                  <IconButton
                                    icon={<Delete20Regular />}
                                    size="sm"
                                    onClick={() => {
                                      setSelectedDomain(row)
                                      setShowDeleteDomainConfirm(true)
                                    }}
                                  />
                                </HStack>
                              )
                            }
                          ]}
                          data={domains}
                        />
                      )}

                      <Banner variant="info" title="Vérification des domaines">
                        Pour une sécurité optimale, vérifiez que vous êtes bien propriétaire des domaines avant de les activer.
                      </Banner>
                    </VStack>
                  </Card>
                </TabPanel>
              )}

              {/* SSO Connections Tab */}
              {isEdit && (
                <TabPanel id="sso" active={activeTab}>
                  <Card padding="lg">
                    <VStack gap="md">
                      <HStack gap="md" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <HStack gap="sm" style={{ alignItems: 'center' }}>
                          <Key20Regular style={{ color: 'var(--brand-primary)' }} />
                          <Heading level={5}>Connexions SSO</Heading>
                        </HStack>
                        <Button
                          icon={<Add20Regular />}
                          size="sm"
                          onClick={() => navigate(`/platform/organizations/${id}/sso/new`)}
                        >
                          Nouvelle connexion
                        </Button>
                      </HStack>

                      <Text size="sm" color="muted">
                        Configurez le SSO (SAML/OIDC) pour chaque application.
                      </Text>

                      {connections.length === 0 ? (
                        <Card variant="outlined" padding="lg">
                          <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                            <Key20Regular style={{ fontSize: '32px', color: 'var(--text-muted)' }} />
                            <Text color="muted">Aucune connexion SSO configurée</Text>
                            <Button
                              icon={<Add20Regular />}
                              size="sm"
                              onClick={() => navigate(`/platform/organizations/${id}/sso/new`)}
                            >
                              Configurer le SSO
                            </Button>
                          </VStack>
                        </Card>
                      ) : (
                        <Table
                          columns={[
                            {
                              key: 'app',
                              header: 'Application',
                              render: (_, row) => (
                                <HStack gap="sm" style={{ alignItems: 'center' }}>
                                  {row.applications?.image_url ? (
                                    <img
                                      src={row.applications.image_url}
                                      alt={row.applications.name}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: 'var(--radius-sm)',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: 'var(--radius-sm)',
                                      backgroundColor: 'var(--brand-primary)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '14px'
                                    }}>
                                      {row.applications?.name?.charAt(0) || '?'}
                                    </div>
                                  )}
                                  <Text weight="medium">{row.applications?.name || 'Unknown'}</Text>
                                </HStack>
                              )
                            },
                            {
                              key: 'protocol',
                              header: 'Protocole',
                              render: (_, row) => (
                                <Badge>{row.protocol.toUpperCase()}</Badge>
                              )
                            },
                            {
                              key: 'status',
                              header: 'Statut',
                              render: (_, row) => (
                                <Badge variant={row.is_active ? 'success' : 'default'}>
                                  {row.is_active ? 'Actif' : 'Inactif'}
                                </Badge>
                              )
                            },
                            {
                              key: 'actions',
                              header: '',
                              render: (_, row) => (
                                <IconButton
                                  icon={<Key20Regular />}
                                  size="sm"
                                  onClick={() => navigate(`/platform/organizations/${id}/sso/${row.id}`)}
                                  title="Configurer"
                                />
                              )
                            }
                          ]}
                          data={connections}
                          onRowClick={(row) => navigate(`/platform/organizations/${id}/sso/${row.id}`)}
                        />
                      )}
                    </VStack>
                  </Card>
                </TabPanel>
              )}

              {/* Members Tab */}
              {isEdit && (
                <TabPanel id="members" active={activeTab}>
                  <Card padding="lg">
                    <VStack gap="md">
                      <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                        <People20Regular style={{ color: 'var(--brand-primary)' }} />
                        <Heading level={5}>Membres</Heading>
                      </HStack>

                      <Text size="sm" color="muted">
                        Utilisateurs qui se sont connectés via le SSO de cette organisation.
                      </Text>

                      {members.length === 0 ? (
                        <Card variant="outlined" padding="lg">
                          <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                            <People20Regular style={{ fontSize: '32px', color: 'var(--text-muted)' }} />
                            <Text color="muted">Aucun membre pour le moment</Text>
                            <Text size="sm" color="muted">
                              Les utilisateurs apparaîtront ici après leur première connexion SSO.
                            </Text>
                          </VStack>
                        </Card>
                      ) : (
                        <Table
                          columns={[
                            {
                              key: 'user_id',
                              header: 'Utilisateur',
                              render: (_, row: OrganizationMember) => (
                                <Text>{row.user_id}</Text>
                              )
                            },
                            {
                              key: 'role',
                              header: 'Rôle',
                              render: (_, row: OrganizationMember) => (
                                <Badge variant={row.role === 'admin' ? 'primary' : 'default'}>
                                  {row.role === 'admin' ? 'Admin' : 'Membre'}
                                </Badge>
                              )
                            },
                            {
                              key: 'created_via',
                              header: 'Source',
                              render: (_, row: OrganizationMember) => (
                                <Badge size="sm">
                                  {row.created_via === 'sso' ? 'SSO' : row.created_via === 'invite' ? 'Invitation' : 'Manuel'}
                                </Badge>
                              )
                            },
                            {
                              key: 'last_login',
                              header: 'Dernière connexion',
                              render: (_, row: OrganizationMember) => (
                                <Text size="sm" color="muted">
                                  {row.last_sso_login_at
                                    ? new Date(row.last_sso_login_at).toLocaleDateString('fr-FR')
                                    : '-'}
                                </Text>
                              )
                            },
                            {
                              key: 'actions',
                              header: '',
                              render: (_, row: OrganizationMember) => (
                                <HStack gap="xs">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleToggleMemberRole(row)}
                                  >
                                    {row.role === 'admin' ? 'Rétrograder' : 'Promouvoir'}
                                  </Button>
                                  <IconButton
                                    icon={<Delete20Regular />}
                                    size="sm"
                                    onClick={() => {
                                      setSelectedMember(row)
                                      setShowRemoveMemberConfirm(true)
                                    }}
                                  />
                                </HStack>
                              )
                            }
                          ]}
                          data={members}
                        />
                      )}
                    </VStack>
                  </Card>
                </TabPanel>
              )}
            </TabPanels>
          </div>

          <HStack gap="md" style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
            <Button
              variant="secondary"
              onClick={() => navigate('/platform/organizations')}
            >
              Annuler
            </Button>
            <Button
              icon={<Save20Regular />}
              loading={saving}
              onClick={handleSubmit}
            >
              {isEdit ? 'Enregistrer' : 'Créer l\'organisation'}
            </Button>
          </HStack>
        </Animate>
      </VStack>

      <ConfirmDialog
        open={showDeleteDomainConfirm}
        onClose={() => setShowDeleteDomainConfirm(false)}
        title="Supprimer le domaine"
        description={`Êtes-vous sûr de vouloir supprimer le domaine "${selectedDomain?.domain}" ? Les utilisateurs de ce domaine ne pourront plus utiliser le SSO.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDeleteDomain}
      />

      <ConfirmDialog
        open={showRemoveMemberConfirm}
        onClose={() => setShowRemoveMemberConfirm(false)}
        title="Retirer le membre"
        description="Êtes-vous sûr de vouloir retirer ce membre de l'organisation ?"
        confirmText="Retirer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleRemoveMember}
      />
    </div>
  )
}
